import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

import { _fetchClientInvoices } from "@/services/clientInvoice";
import { handleAPICatch } from "@/utilities/utils";

import { formatInvoicesList } from "../utilities/dataFormatter";

const initialState = {
  isLoading: false,
  isPaginationLoading: false,
  clientInvoices: [],
  selectedInvoices: [],
  filters: {
    search: "",
    sessionType: [],
    // referenceNo: "",
    contractReferenceNo: "",
    invoiceReferenceNo: "",
    invoiceStatus: [],
    licenseNo: null,
    candidateRole: null,
    candidateId: [],
    invoiceDateFrom: "",
    invoiceDateTo: "",
    dueDateFrom: "",
    dueDateTo: "",
  },
  totalCount: 0,
  isSelectedAll: false,
  oldFilters: {},
  pagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  sorting: {
    sortByColumn: "",
    sortByOrder: "",
  },
  toggleSelected: false,
  totalAmt: "",
  showFilters: false,
};

export const fetchClientInvoices = createAsyncThunk(
  "clientInvoice/fetchClientInvoices",
  async (data, { dispatch, getState }) => {
    const {
      clientInvoices: { filters, pagination, sorting, clientInvoices },
    } = getState();

    if (_.isEmpty(data)) {
      data = {
        ...filters,
      };
    }
    if (pagination.isLastPage) {
      return dispatch(setLoading(false));
    }

    dispatch(setPaginationLoading(pagination.currentPage !== 1));
    dispatch(toggleSelectedAll(false));

    const _data = {
      ...data,
      currentPage: pagination.currentPage,
      ...sorting,
    };
    const response = _fetchClientInvoices(_data);
    response
      .then((res) => {
        const {
          data: { invoice_list, total_amount: totalAmt, meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        let data = formatInvoicesList(invoice_list);
        dispatch(
          updatePagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );

        let prevList = currentPage === 1 ? [] : _.uniq(clientInvoices);

        dispatch(updateTotalCount(totalCount));

        dispatch(updateTotalAmt(totalAmt));

        let firstinvoice_id = data.length > 0 ? data[0].invoice_id : null;
        if (
          prevList.length > 0 &&
          prevList.find((item) => item.invoice_id === firstinvoice_id) !==
            undefined
        ) {
          //
        } else {
          dispatch(updateClientInvoices([...prevList, ...data]));
        }
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setLoading(false));
          dispatch(setPaginationLoading(false));
        }, 10);
      });
  },
);

const clientInvoiceSlice = createSlice({
  name: "clientInvoice",
  initialState,
  reducers: {
    /**
     * Updates the client invoices in the state with the provided payload.
     *
     * @param {type} state - the state object
     * @param {type} action - the action object containing the payload
     * @return {type} undefined
     */
    updateClientInvoices: (state, action) => {
      state.clientInvoices = action.payload;
    },
    /**
     * Sets the loading state based on the action payload.
     *
     * @param {type} state - the current state
     * @param {type} action - the action object
     * @return {type} undefined
     */
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPaginationLoading(state, action) {
      state.isPaginationLoading = action.payload; // New reducer for pagination loading
    },
    /**
     * updateSelectedInvoices - Updates the selected invoices in the state.
     *
     * @param {Object} state - The current state object
     * @param {Object} action - The action object containing the payload
     * @return {void}
     */
    updateSelectedInvoices: (state, action) => {
      state.selectedInvoices = action.payload;
      if (action.payload.length === 0) {
        state.isSelectedAll = false;
      }
    },
    deSelectAllInvoices: (state) => {
      state.selectedInvoices = [];
      state.isSelectedAll = false;
    },
    /**
     * Updates the filters in the state based on the provided action.
     *
     * @param {object} state - The current state object
     * @param {object} action - The action object containing payload for updating filters
     */
    updateFilters: (state, action) => {
      if (_.isEmpty(action.payload)) {
        state.filters = {
          search: state.filters.search,
          ...initialState.filters,
        };
        return;
      } else {
        if (action.payload.reset) {
          state.filters = initialState.filters;
        } else {
          state.filters = { ...state.filters, ...action.payload };
        }
      }
    },
    /**
     * Function to show the filter modal based on the state and action.
     *
     * @param {type} state - description of state parameter
     * @param {type} action - description of action parameter
     * @return {type} description of return value
     */
    showFilterModal: (state, action) => {
      if (action.payload) {
        state.oldFilters = state.filters;
      }
      state.showFilters = action.payload;
    },
    /**
     * Updates the pagination state with the provided payload.
     *
     * @param {object} state - The current state object
     * @param {object} action - The action object containing the payload
     * @return {void}
     */
    updatePagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
    updateSorting: (state, action) => {
      state.sorting = action.payload;
    },
    /**
     * reStateFilters: A function that updates the filters in the state to the old filters.
     *
     * @param {object} state - The current state object
     * @param {object} action - The action object
     * @return {void}
     */
    reStateFilters: (state, action) => {
      state.filters = state.oldFilters;
    },
    /**
     * Update the value of toggleSelected in the state object.
     *
     * @param {object} state - the state object
     * @return {void}
     */
    updateToggleSelected: (state) => {
      state.toggleSelected = !state.toggleSelected;
    },
    resetPage: (state) => {
      state.pagination = initialState.pagination;
      state.filters = initialState.filters;
      state.clientInvoices = [];
      state.isLoading = true;
      state.selectedInvoices = [];
      state.toggleSelected = false;
    },
    updateTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    updateTotalAmt: (state, action) => {
      state.totalAmt = action.payload;
    },
    toggleSelectedAll: (state, action) => {
      state.isSelectedAll = action.payload;
    },
  },
});

export const {
  updateClientInvoices,
  setLoading,
  setPaginationLoading,
  updateSelectedInvoices,
  deSelectAllInvoices,
  updateFilters,
  showFilterModal,
  updateSorting,
  updatePagination,
  reStateFilters,
  updateToggleSelected,
  resetPage,
  updateTotalCount,
  updateTotalAmt,
  toggleSelectedAll,
} = clientInvoiceSlice.actions;

export default clientInvoiceSlice.reducer;
